import { useEffect, useState, useRef } from "react";
import { useUserContext } from "../../context/UserContext";

const stepsList = [
    "Connect Main PCB",
    "Connect Test PCB",
    "Validate to Device",
    "Emergency Created",
    "Audio Played",
    "Notification Triggered",
    "Notification In-App",
    "Did you raise a ticket?",
    "Ticket Raised",
    "Emergency Resolved",
    "Audio Played (Resolution)",
    "Resolution Notification Triggered",
    "Resolution Notification In-App",
    "Ticket Closed",
];

const stepsList2 = [
    "Connect Main PCB",
    "Connect test PCB",
    "Validate to Device",
    "Emergency Undecided",
    "Undecided Notification Triggered",
    "Undecided Notification In-App",
    "Audio Played (Master Program)",
    "Master Program Initiated", // 21 termie
    "Audio Played (Master Program Completed)",
    "Master Program Completed", // 24 termin
    "Emergency Created",
    "Audio Played (Emergency)",
    "Notification Triggered",
    "Notification In-App",
    "Ticket Raised",
    "Emergency Resolved",
    "Audio Played (Resolution)",
    "Resolution Notification Triggered",
    "Resolution Notification In-App",
    "Ticket Closed",
];

const PCBTestStatusDisplay = () => {
    const { socket, testScenario, isPcbTesting } = useUserContext();
    const [steps, setSteps] = useState([]);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [showTicketButtons, setShowTicketButtons] = useState(false);
    const [isEmergencyResolvedCompleting, setIsEmergencyResolvedCompleting] = useState(false);
    const timerRef = useRef(null);

    const TICKET_PROMPT_STEP_NAME = "Did you raise a ticket?";
    const TICKET_RAISED_STEP_NAME = "Ticket Raised";
    const EMERGENCY_RESOLVED_STEP_NAME = "Emergency Resolved";

    const [ticketPromptStepIndex, setTicketPromptStepIndex] = useState(-1);
    const [ticketRaisedStepIndex, setTicketRaisedStepIndex] = useState(-1);
    const [emergencyResolvedStepIndex, setEmergencyResolvedStepIndex] = useState(-1);

    useEffect(() => {
        let activeStepsList = [];
        if (testScenario === "ps1" || testScenario === "tds1") {
            activeStepsList = stepsList;
        } else if (testScenario === "ps2" || testScenario === "fs1" || testScenario === "fs2" || testScenario === "fs3" || testScenario === "tds2" || testScenario === "tds3") {
            activeStepsList = stepsList2;
        }

        setSteps(
            activeStepsList.map((step) => ({
                name: step,
                status: "Waiting",
                duration: 0,
                comments: "—",
                result: "Pending",
            }))
        );

        setTicketPromptStepIndex(activeStepsList.indexOf(TICKET_PROMPT_STEP_NAME));
        setTicketRaisedStepIndex(activeStepsList.indexOf(TICKET_RAISED_STEP_NAME));
        setEmergencyResolvedStepIndex(activeStepsList.indexOf(EMERGENCY_RESOLVED_STEP_NAME));

        setCurrentStepIndex(0);
        setShowTicketButtons(false);
        setIsEmergencyResolvedCompleting(false);
    }, [testScenario]);

    useEffect(() => {
        if (!isPcbTesting || currentStepIndex >= steps.length || steps.length === 0) {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
            return;
        }

        if (currentStepIndex === ticketPromptStepIndex && steps[currentStepIndex]?.status !== "Completed") {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
            setShowTicketButtons(true);
            return;
        }

        if (
            steps[currentStepIndex]?.status !== "Completed" &&
            steps[currentStepIndex]?.status !== "Failed" &&
            !isEmergencyResolvedCompleting
        ) {
            timerRef.current = setInterval(() => {
                setSteps((prevSteps) =>
                    prevSteps.map((step, idx) =>
                        idx === currentStepIndex
                            ? { ...step, duration: step.duration + 1 }
                            : step
                    )
                );
            }, 1000);
        }

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [currentStepIndex, isPcbTesting, steps, ticketPromptStepIndex, isEmergencyResolvedCompleting]);

    useEffect(() => {
        if (!socket) return;

        const handleStepUpdate = (data) => {
            const { stepIndex, status, comments, result } = data;

            setSteps((prevSteps) => {
                const newSteps = prevSteps.map((step, idx) =>
                    idx === stepIndex
                        ? {
                              ...step,
                              status: status || "Completed",
                              comments: comments || "—",
                              result: result || "Passed",
                          }
                        : step
                );

                if (
                    stepIndex === emergencyResolvedStepIndex &&
                    (status === "Completed" || status === "Failed")
                ) {
                    setIsEmergencyResolvedCompleting(true);
                }
                else if (
                    (status === "Completed" || status === "Failed") &&
                    stepIndex + 1 < newSteps.length
                ) {
                    if (stepIndex !== ticketPromptStepIndex) {
                        setCurrentStepIndex(stepIndex + 1);
                    }
                }
                return newSteps;
            });
        };

        const handleResolutionAudioPlayed = (data) => {
            const resolutionAudioPlayedIndex = steps.findIndex(step => step.name === "Audio Played (Resolution)");
            if (resolutionAudioPlayedIndex !== -1) {
                setSteps(prevSteps => prevSteps.map((step, idx) =>
                    idx === resolutionAudioPlayedIndex
                        ? { ...step, status: "Completed", comments: data.comments || "Resolution audio played", result: data.result || "Passed" }
                        : step
                ));
                setCurrentStepIndex(resolutionAudioPlayedIndex + 1);
            }
        };

        const handleResolutionNotificationTriggered = (data) => {
            const resolutionNotificationTriggeredIndex = steps.findIndex(step => step.name === "Resolution Notification Triggered");
            if (resolutionNotificationTriggeredIndex !== -1) {
                setSteps(prevSteps => prevSteps.map((step, idx) =>
                    idx === resolutionNotificationTriggeredIndex
                        ? { ...step, status: "Completed", comments: data.comments || "Resolution notification triggered", result: data.result || "Passed" }
                        : step
                ));
                setCurrentStepIndex(resolutionNotificationTriggeredIndex + 1);
            }
        };

        const handleResolutionNotificationInApp = (data) => {
            const resolutionNotificationInAppIndex = steps.findIndex(step => step.name === "Resolution Notification In-App");
            if (resolutionNotificationInAppIndex !== -1) {
                setSteps(prevSteps => prevSteps.map((step, idx) =>
                    idx === resolutionNotificationInAppIndex
                        ? { ...step, status: "Completed", comments: data.comments || "Resolution notification in-app", result: data.result || "Passed" }
                        : step
                ));
                setCurrentStepIndex(resolutionNotificationInAppIndex + 1);
            }
        };

        const handleTicketClosed = (data) => {
            const ticketClosedIndex = steps.findIndex(step => step.name === "Ticket Closed");
            if (ticketClosedIndex !== -1) {
                setSteps(prevSteps => prevSteps.map((step, idx) =>
                    idx === ticketClosedIndex
                        ? { ...step, status: "Completed", comments: data.comments || "Ticket closed", result: data.result || "Passed" }
                        : step
                ));
                setIsEmergencyResolvedCompleting(false);
                setCurrentStepIndex(ticketClosedIndex + 1);
            }
        };

        socket.on("stepUpdate", handleStepUpdate);
        socket.on("resolutionAudioPlayed", handleResolutionAudioPlayed);
        socket.on("resolutionNotificationTriggered", handleResolutionNotificationTriggered);
        socket.on("resolutionNotificationInApp", handleResolutionNotificationInApp);
        socket.on("ticketClosed", handleTicketClosed);

        return () => {
            socket.off("stepUpdate", handleStepUpdate);
            socket.off("resolutionAudioPlayed", handleResolutionAudioPlayed);
            socket.off("resolutionNotificationTriggered", handleResolutionNotificationTriggered);
            socket.off("resolutionNotificationInApp", handleResolutionNotificationInApp);
            socket.off("ticketClosed", handleTicketClosed);
        };
    }, [socket, ticketPromptStepIndex, emergencyResolvedStepIndex, steps]);

    const handleYesClick = () => {
        if (socket) {
            socket.emit("ticket_raised_status", "yes");
        }
        setSteps((prevSteps) =>
            prevSteps.map((step, idx) => {
                if (idx === ticketPromptStepIndex) {
                    return { ...step, status: "Completed", comments: "User indicated 'Yes'", result: "Passed" };
                } else if (idx === ticketRaisedStepIndex) {
                    return { ...step, status: "Completed", comments: "Ticket was raised", result: "Passed" };
                }
                return step;
            })
        );
        setShowTicketButtons(false);
        setCurrentStepIndex(ticketRaisedStepIndex + 1);
    };

    const handleNoClick = () => {
        if (socket) {
            socket.emit("ticket_raised_status", "no");
        }
        setSteps((prevSteps) =>
            prevSteps.map((step, idx) => {
                if (idx === ticketPromptStepIndex) {
                    return { ...step, status: "Completed", comments: "User indicated 'No'", result: "Passed" };
                } else if (idx === ticketRaisedStepIndex) {
                    return { ...step, status: "Completed", comments: "Ticket was not raised", result: "Passed" };
                }
                return step;
            })
        );
        setShowTicketButtons(false);
        setCurrentStepIndex(ticketRaisedStepIndex + 1);
    };

    if (!isPcbTesting) return null;

    const getStatusColor = (status) => {
        switch (status) {
            case "Completed": return "bg-green-100 text-green-800";
            case "In Progress": return "bg-blue-100 text-blue-800";
            case "Failed": return "bg-red-100 text-red-800";
            case "Waiting": return "bg-gray-100 text-gray-800";
            default: return "bg-gray-100 text-gray-800";
        }
    };

    const getResultColor = (result) => {
        switch (result) {
            case "Passed": return "text-green-600 font-semibold";
            case "Failed": return "text-red-600 font-semibold";
            default: return "text-gray-500";
        }
    };

    const getCommentColor = (comment) => {
        return comment === "—" ? "text-gray-400 italic" : "text-gray-700";
    };

    const renderStepsTable = (currentSteps) => (
        <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Check Description
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Time (s)
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Details
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Outcome
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {currentSteps.map((step, idx) => (
                            <tr
                                key={idx}
                                className={
                                    idx === currentStepIndex && step.status !== "Completed" && step.status !== "Failed"
                                        ? "bg-blue-50"
                                        : "hover:bg-gray-50"
                                }
                            >
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    {step.name}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(step.status)}`}>
                                        {step.status}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                                    {step.duration}s
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    {idx === ticketPromptStepIndex && showTicketButtons && step.status !== "Completed" ? (
                                        <div className="flex space-x-2">
                                            <button
                                                onClick={handleYesClick}
                                                className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                                            >
                                                Yes
                                            </button>
                                            <button
                                                onClick={handleNoClick}
                                                className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                                            >
                                                No
                                            </button>
                                        </div>
                                    ) : (
                                        <span className={`${getCommentColor(step.comments)}`}>
                                            {step.comments}
                                        </span>
                                    )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    <span className={`${getResultColor(step.result)}`}>
                                        {step.result}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <div className="mb-3">
                    <div className="flex justify-between items-center">
                        <h3 className="text-sm font-medium text-gray-700">Test Progress</h3>
                        <span className="text-xs font-medium text-gray-500">
                            {currentStepIndex + (currentSteps[currentStepIndex]?.status === "Completed" || currentSteps[currentStepIndex]?.status === "Failed" ? 1 : 0)}/{currentSteps.length} steps
                        </span>
                    </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${((currentStepIndex + (currentSteps[currentStepIndex]?.status === "Completed" || currentSteps[currentStepIndex]?.status === "Failed" ? 1 : 0)) / currentSteps.length) * 100}%` }}
                    ></div>
                </div>
            </div>
        </div>
    );

    if (testScenario === "ps1" || testScenario === "tds1") {
        return renderStepsTable(steps);
    } else if (testScenario === "ps2" || testScenario === "fs1" || testScenario === "fs2" || testScenario === "fs3" || testScenario === "tds2" || testScenario === "tds3") {
        return renderStepsTable(steps);
    } else {
        return null;
    }
};

export default PCBTestStatusDisplay;